import { useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { resolveDecisionFocus } from "./decisionFlows";
import { resolveDeliveryFocus } from "./deliveryFlows";
import { resolveGovernanceFocus } from "./governanceFlows";
import { resolveAssessmentFocus } from "./assessmentFlows";

/** Map legacy ?step= spine links and explicit ?focus= to the active sub-flow pane. */
export function resolveSubFlowFocus(pathname, search, defaultFocus, param = "focus") {
  const params = new URLSearchParams(search);
  const explicit = params.get(param);
  if (explicit) {
    if (pathname === "/decision") return resolveDecisionFocus(explicit);
    if (pathname === "/delivery") return resolveDeliveryFocus(explicit);
    if (pathname === "/governance") return resolveGovernanceFocus(explicit);
    if (pathname === "/assessment") return resolveAssessmentFocus(explicit, params.get("step"));
    if (pathname === "/enterprise" && (explicit === "monitor" || explicit === "design")) {
      return explicit === "monitor" ? "connectors" : "strategy";
    }
    return explicit;
  }

  const step = params.get("step");
  if (pathname === "/assessment") {
    return resolveAssessmentFocus(null, step);
  }
  if (pathname === "/governance") {
    if (step === "plan") return "wave";
    if (step === "govern") return "gates-early";
  }
  if (pathname === "/enterprise") {
    if (step === "monitor") return "monitor";
    if (step === "design") return "strategy";
  }
  const disclosure = params.get("disclosure");
  if (pathname === "/delivery" && disclosure) {
    return resolveDeliveryFocus(disclosure);
  }
  return defaultFocus;
}

export function subflowHref(pathname, appId, focusId, param = "focus") {
  const base = pathname || "/";
  if (!focusId || focusId === "overview") {
    if (appId) return `${base}?app=${encodeURIComponent(appId)}`;
    return base;
  }
  const params = new URLSearchParams();
  if (appId) params.set("app", appId);
  params.set(param, focusId);
  return `${base}?${params.toString()}`;
}

/** One URL query key per page — shows a single concrete sub-topic at a time. */
export function useSubFlow(defaultFocus, param = "focus") {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const focus = resolveSubFlowFocus(location.pathname, location.search, defaultFocus, param);

  const setFocus = useCallback(
    (next, { replace = false } = {}) => setParams((prev) => {
      const copy = new URLSearchParams(prev);
      if (!next || next === "overview") {
        copy.delete(param);
        copy.delete("step");
      } else {
        copy.set(param, next);
        copy.delete("step");
      }
      return copy;
    }, { replace }),
    [param, setParams],
  );

  return { focus, setFocus };
}

export function withAppFocus(path, appId, focus) {
  const [base, query = ""] = String(path || "/").split("?");
  const params = new URLSearchParams(query);
  if (appId) params.set("app", appId);
  if (focus && focus !== "overview") params.set("focus", focus);
  else params.delete("focus");
  const encoded = params.toString();
  return encoded ? `${base}?${encoded}` : base;
}
